import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocation } from 'react-router-dom';
import { getAndValidateToken, handleInvalidToken } from '../utils/tokenValidation';

const DataUserContext = createContext({
    isAuth: false,
    setIsAuth: () => { },
    setFullName: () => { },
    token: '',
    fullName: '',
    patientId: '',
    doctorId: '',
    clinicId: '',
    adminId: '',
    service: '',
    role: '',
});

export const useDataUser = () => useContext(DataUserContext);

// Verificar si estamos en una ruta pública donde no necesitamos validar token
const publicRoutes = [
    '/login', 
    '/', 
    '/registro', 
    '/registro-gratuito', 
    '/registro-pagado',
    '/registro-gratuito-usuario',
    '/registro-pagado-usuario',
    '/registro-afiliado',
    '/registro-afiliado-epayco',
    '/autorizaciondatos',
    '/autorizaciondatosm',
    '/terminos-y-condiciones',
    '/cita-temporal',
    '/consentimiento',
    '/consentimiento-externo',
    '/video-call-droxy',
    '/oauth/google/callback',
    '/oauth/linkedin/callback'
];

const isPublicRoute = (pathname) => {
    return publicRoutes.includes(pathname) || 
           pathname.startsWith('/cita-temporal/') ||
           pathname.startsWith('/consentimiento/') ||
           pathname.startsWith('/consentimiento-externo/') ||
           pathname.startsWith('/video-call-droxy/') ||
           pathname.startsWith('/oauth/');
};

const STORAGE_KEY_FULL_NAME = 'salutio_user_full_name';

export function DataUserProvider({ children }) {
    const location = useLocation();
    // Override de fullName (cuando el usuario actualiza el nombre en el perfil). Persistido en localStorage para que sobreviva al refresh.
    const [fullNameOverride, setFullNameOverride] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY_FULL_NAME);
            return (stored && stored.trim()) ? stored.trim() : null;
        } catch (e) {
            return null;
        }
    });

    const setFullName = (name) => {
        const value = (name && String(name).trim()) ? String(name).trim() : null;
        setFullNameOverride(value);
        try {
            if (value) localStorage.setItem(STORAGE_KEY_FULL_NAME, value);
            else localStorage.removeItem(STORAGE_KEY_FULL_NAME);
        } catch (e) {}
    };

    const [isAuth, setIsAuth] = useState(() => {
        // En el estado inicial, verificar si hay token y si no estamos en ruta pública
        const currentPath = window.location.pathname;
        const hasToken = !!localStorage.getItem('token');
        return hasToken && !isPublicRoute(currentPath);
    });

    // Manejar cambios de ruta dinámicamente
    useEffect(() => {
        const currentPath = location.pathname;
        const hasToken = !!localStorage.getItem('token');
        
        // Si estamos en una ruta pública pero tenemos token, mantener el contexto actualizado
        // Esto es importante para transiciones desde rutas públicas a protegidas
        if (isPublicRoute(currentPath)) {
            // No establecer isAuth en false si hay token, solo no requerir autenticación para la ruta
            // El token seguirá siendo válido y el contexto mantendrá los datos del usuario
            if (!hasToken) {
                setIsAuth(false);
            }
            // Si hay token, mantener isAuth como está (no forzar a false)
        } else {
            // Si no es ruta pública, verificar token
            setIsAuth(hasToken);
        }
    }, [location.pathname]);

    const userData = useMemo(() => {
        // Verificar si hay token, incluso si isAuth es false (puede estar en ruta pública)
        const token = localStorage.getItem('token');
        const hasToken = !!token;
        
        // Si hay token, intentar cargar los datos del usuario
        if (hasToken) {
            // Usar la utilidad de validación de token
            const tokenValidation = getAndValidateToken();
            
            if (!tokenValidation.isValid) {
                console.error('Token inválido:', tokenValidation.error);
                setIsAuth(false);
                handleInvalidToken(tokenValidation);
                return {
                    token: '',
                    service: '',
                    fullName: '',
                    role: '',
                    patientId: '',
                    doctorId: '',
                    clinicId: '',
                    adminId: '',
                };
            }

            const { decoded } = tokenValidation;
            
            // Si el token es válido pero isAuth es false (ruta pública), 
            // aún así devolver los datos del usuario para que estén disponibles
            const userDataResult = {
                token: token,
                role: decoded.role || '',
                fullName: fullNameOverride != null ? fullNameOverride : (decoded.fullName || ''),
                service: decoded.service || '',
                patientId: decoded.patientId || '',
                doctorId: decoded.doctorId || '',
                clinicId: decoded.clinicId || '',
                adminId: decoded.adminId || '',
            };
            
            // Si tenemos datos válidos y no estamos en una ruta pública, actualizar isAuth
            if (decoded.role && isAuth === false) {
                const currentPath = location.pathname;
                
                // Solo actualizar isAuth si no estamos en una ruta pública
                if (!isPublicRoute(currentPath)) {
                    setIsAuth(true);
                }
            }
            
            return userDataResult;
        }
        
        // Si no hay token y no está autenticado, devolver datos vacíos
        return {
            token: '',
            service: '',
            fullName: fullNameOverride || '',
            role: '',
            patientId: '',
            doctorId: '',
            clinicId: '',
            adminId: '',
        }
    }, [isAuth, location.pathname, fullNameOverride]);

    // Limpiar override de nombre al cerrar sesión (para que otro usuario no vea el nombre anterior)
    useEffect(() => {
        if (!isAuth) {
            setFullNameOverride(null);
            try { localStorage.removeItem(STORAGE_KEY_FULL_NAME); } catch (e) {}
        }
    }, [isAuth]);

    return (
        <DataUserContext.Provider value={{ isAuth, setIsAuth, setFullName, ...userData }}>
            {children}
        </DataUserContext.Provider>
    )
}
